import { useState, type FormEvent } from 'react';
import { useAuth } from '@/hooks/useAuth';

interface LoginFormProps {
  readonly onSuccess: () => void;
  readonly onSignUpClick: () => void;
}

const inputStyle = {
  marginTop: 'var(--space-1)',
  display: 'block',
  width: '100%',
  borderRadius: 'var(--radius-md)',
  border: '1px solid var(--border-default)',
  padding: 'var(--space-2) var(--space-3)',
  minHeight: '44px',
  fontSize: 'var(--text-base)',
  backgroundColor: 'var(--bg-surface)',
  color: 'var(--text-primary)',
  outline: 'none',
  transition: 'border-color var(--duration-fast) var(--ease-out)',
} as const;

const labelStyle = {
  display: 'block',
  fontSize: 'var(--text-sm)',
  fontWeight: 'var(--font-weight-medium)',
  color: 'var(--text-secondary)',
} as const;

export const LoginForm = ({ onSuccess, onSignUpClick }: LoginFormProps) => {
  const { signIn } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      await signIn(email, password);
      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sign in failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
      <h2
        style={{
          fontSize: 'var(--text-2xl)',
          fontWeight: 'var(--font-weight-bold)',
          color: 'var(--text-primary)',
          letterSpacing: 'var(--tracking-tight)',
        }}
      >
        Sign In
      </h2>

      {error && (
        <div
          role="alert"
          style={{
            borderRadius: 'var(--radius-md)',
            backgroundColor: 'var(--color-error-50)',
            padding: 'var(--space-3)',
            fontSize: 'var(--text-sm)',
            color: 'var(--color-error-700)',
          }}
        >
          {error}
        </div>
      )}

      <div>
        <label htmlFor="email" style={labelStyle}>Email</label>
        <input
          id="email"
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="focus-ring"
          style={inputStyle}
          placeholder="you@example.com"
          onFocus={(e) => { e.currentTarget.style.borderColor = 'var(--border-focus)'; }}
          onBlur={(e) => { e.currentTarget.style.borderColor = 'var(--border-default)'; }}
        />
      </div>

      <div>
        <label htmlFor="password" style={labelStyle}>Password</label>
        <input
          id="password"
          type="password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="focus-ring"
          style={inputStyle}
          onFocus={(e) => { e.currentTarget.style.borderColor = 'var(--border-focus)'; }}
          onBlur={(e) => { e.currentTarget.style.borderColor = 'var(--border-default)'; }}
        />
      </div>

      <button
        type="submit"
        disabled={isSubmitting}
        className="focus-ring"
        style={{
          width: '100%',
          borderRadius: 'var(--radius-md)',
          backgroundColor: 'var(--color-primary-600)',
          padding: 'var(--space-3) var(--space-4)',
          minHeight: '44px',
          color: 'var(--text-inverse)',
          fontWeight: 'var(--font-weight-medium)',
          border: 'none',
          cursor: isSubmitting ? 'not-allowed' : 'pointer',
          opacity: isSubmitting ? 0.5 : 1,
          transition: 'background-color var(--duration-fast) var(--ease-out)',
        }}
        onMouseEnter={(e) => { if (!isSubmitting) e.currentTarget.style.backgroundColor = 'var(--color-primary-500)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'var(--color-primary-600)'; }}
      >
        {isSubmitting ? 'Signing in...' : 'Sign In'}
      </button>

      <p
        className="text-center"
        style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)' }}
      >
        Don't have an account?{' '}
        <button
          type="button"
          onClick={onSignUpClick}
          style={{
            color: 'var(--text-accent)',
            fontWeight: 'var(--font-weight-medium)',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
          }}
        >
          Sign Up
        </button>
      </p>
    </form>
  );
};
